<script setup>
/**
 * AgentCatalogView
 * Agent-specific catalog view with client context override
 * Loads catalog for a specific client (by makat) that the agent is viewing
 * Layout matches the regular CatalogView
 */
import { ref, computed, onMounted, onUnmounted, onActivated, onDeactivated, watch } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { useBackButtonClose } from '../../../core/composables/useBackButtonClose.js'
import { storeToRefs } from 'pinia'
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore'
import { getFirebaseDb } from '@core/firebase/firebase'
import { useAuthStore } from '../../../app/store/auth.js'
import { useCatalogStore } from '../../../app/store/catalog.js'
import { useCartStore } from '../../../app/store/cart.js'
import { useAssortmentRequestStore } from '../../../app/store/assortmentRequest.js'
import { getClientByMakat } from '../../../core/services/clients.js'
import { ROUTES } from '../../../core/config/constants.js'
import { useSwipeNavigation } from '../../../core/composables/useSwipeNavigation.js'
import { getPromoMakats } from '../../../core/utils/promoCalc.js'
import CatalogLoader from '../../components/catalog/CatalogLoader.vue'
import DepartmentsMenu from '../../components/catalog/DepartmentsMenu.vue'
import CatalogItemCard from '../../components/catalog/CatalogItemCard.vue'
import PromotionCard from '../../components/catalog/PromotionCard.vue'
import CatalogFilters from '../../components/catalog/CatalogFilters.vue'
import ItemDialog from '../../components/catalog/item-dialog/ItemDialog.vue'
import MobileSubcategoryBar from '../../components/catalog/MobileSubcategoryBar.vue'
import DesktopCategoryStrip from '../../components/catalog/DesktopCategoryStrip.vue'
import SwipeTutorial from '../../components/catalog/SwipeTutorial.vue'
import MobileFiltersSheet from '../../components/catalog/MobileFiltersSheet.vue'
import ClientGoalsInfoPage from '../../components/agents/ClientGoalsInfoPage.vue'
import ImmersiveSwitchButton from '../../components/catalog/ImmersiveSwitchButton.vue'

defineOptions({ name: 'AgentCatalogView' })

// ============================================
// Router
// ============================================
const route = useRoute()
const router = useRouter()

// ============================================
// Stores
// ============================================
const authStore = useAuthStore()
const catalogStore = useCatalogStore()
const cartStore = useCartStore()
const assortmentRequestStore = useAssortmentRequestStore()
const { count: niaSelectionCount, isEmpty: niaSelectionEmpty } = storeToRefs(assortmentRequestStore)

// ============================================
// Agent Context State
// ============================================
const isLoadingClient = ref(true)
const clientError = ref(null)
const selectedClient = ref(null)
const isAuthorized = ref(false)
const showCartWarning = ref(false)
const isBootstrapRunning = ref(false)

// Goals info page state
const showInfoPage = ref(false)
const relevantGoals = ref([])
const isLoadingGoals = ref(false)

// Set of makats that appear in active goals (for card highlight)
const goalMakatsSet = computed(() => {
  const set = new Set()
  for (const goal of relevantGoals.value) {
    const products = goal._matchingProducts || goal.products || []
    for (const p of products) {
      if (p.makat) set.add(String(p.makat))
    }
  }
  return set
})

// KeepAlive active state guard
const isComponentActive = ref(true)
// Track last bootstrapped context to detect real client/agent changes vs child-route returns
const lastActivatedContext = ref(null)

// ============================================
// Computed: Client Makat from Route
// ============================================
const clientMakat = computed(() => route.params.clientMakat)

// ============================================
// Computed: Role checks
// ============================================
const { isAdmin } = storeToRefs(authStore)

// ============================================
// Computed: Agent Number from User Profile
// Uses effectiveAgentNumber which includes admin impersonation override
// ============================================
const userAgentNumber = computed(() => {
  // Use effectiveAgentNumber which handles both normal agent number and admin override
  const agentValue = authStore.effectiveAgentNumber
  if (agentValue === null || agentValue === undefined || agentValue === '') {
    return null
  }
  const num = Number(agentValue)
  return isNaN(num) ? null : num
})

// ============================================
// Catalog Store Refs
// ============================================
const {
  isBootstrapping,
  bootstrapError,
  departments,
  departmentsWithPromo,
  visibleDepartments,
  visibleCurrentDeptSubs,
  visibleSubsByDeptIdObject,
  departmentsLoading,
  currentDeptSubs,
  subsByDepartmentId,
  subsByDeptIdObject,
  subsLoading,
  selectedDepartmentId,
  selectedDepartmentName,
  selectedSubDepartmentId,
  selectedSubDepartmentName,
  searchQuery,
  isSearchActive,
  searchResults,
  visibleItems,
  showSearchHelper,
  itemsArray,
  allDisplayableItems,
  catalogMode,
  linkedClient,
  activeFilterCount,
  singlePromoItems,
  multiItemPromotions,
  firstOrderMultiPromos,
  regularMultiPromos,
  firstOrderSinglePromoItems,
  regularSinglePromoItems,
  isMobileView,
  isTabletView,
  isPCView,
  validItemsPerRow,
  isGridMode,
  gridStyle,
} = storeToRefs(catalogStore)

// Aliases for template compatibility
const isMobile = isMobileView
const isTablet = isTabletView
const isPC = isPCView

const PROMO_DEPARTMENT_ID = catalogStore.PROMO_DEPARTMENT_ID

// ============================================
// Local UI State
// ============================================
const isMobileDepartmentsOpen = ref(false)
const desktopSidebarCollapsed = ref(false)
const menuViewMode = ref('departments')
const departmentSearchQuery = ref('')
const productsContainerRef = ref(null)
const searchOpenMobile = ref(false)
const filtersOpenMobile = ref(false)
const pendingAutoSelectDept = ref(null)

// Item Dialog state
const isItemDialogOpen = ref(false)
const selectedMakat = ref(null)

// Computed: Client name font size (auto-fit based on length)
const clientNameFontSize = computed(() => {
  const name = selectedClient.value?.name || ''
  const len = name.length
  if (len <= 12) return '14px'
  if (len <= 18) return '13px'
  if (len <= 25) return '12px'
  if (len <= 32) return '11px'
  return '10px'
})

// ============================================
// Computed: Show Prices
// ============================================
const showPrices = computed(() => {
  if (selectedClient.value && selectedClient.value.prices === false) {
    return false
  }
  return true
})

// ============================================
// Swipe Navigation
// ============================================
const swipeEnabled = computed(
  () => !isSearchActive.value && !isBootstrapping.value && !isItemDialogOpen.value
)

const { swipeStyle, transitionLabel } = useSwipeNavigation({
  containerRef: productsContainerRef,
  departments: visibleDepartments,
  getSubsForDept: (deptId) => catalogStore.getVisibleSubsForDept(deptId),
  selectedDepartmentId,
  selectedSubDepartmentId,
  promoDepartmentId: PROMO_DEPARTMENT_ID,
  onNavigate: (subId, subName, deptId, deptName) => {
    catalogStore.selectSubDepartment(subId, subName, deptId, deptName)
    menuViewMode.value = 'subs'
    if (productsContainerRef.value) {
      productsContainerRef.value.scrollTo({ top: 0, behavior: 'instant' })
    }
  },
  enabled: swipeEnabled,
  loadAllSubs: () => catalogStore.loadAllSubs(),
})

// ============================================
// Helper Functions
// ============================================
function scrollToTop() {
  if (productsContainerRef.value) {
    productsContainerRef.value.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

function navigateNotInAssortment(useReplace = false) {
  // Snapshot client info before unmount clears agentOverrideClient
  if (selectedClient.value) {
    assortmentRequestStore.setClientInfo(selectedClient.value)
  }
  const method = useReplace ? 'replace' : 'push'
  router[method]({
    name: ROUTES.AGENTS_NOT_IN_ASSORTMENT,
    params: { clientMakat: route.params.clientMakat },
  })
}

function openItemDialog(makat) {
  // Block dialog for not-in-assortment items
  const item = catalogStore.itemsByMakat.get(makat)
  if (!item) {
    const niaItem = catalogStore.allDisplayableItems.find((i) => i.makat === makat)
    if (niaItem?._notInAssortment) return
  }

  // Outside promotions dept: if item belongs to a grouped promotion, navigate to it
  if (selectedDepartmentId.value !== PROMO_DEPARTMENT_ID) {
    const groupedPromo = multiItemPromotions.value.find((p) => getPromoMakats(p).includes(makat))
    if (groupedPromo) {
      router.push({
        name: ROUTES.AGENTS_PROMOTION_DETAIL,
        params: { clientMakat: clientMakat.value, promoId: groupedPromo.id },
      })
      return
    }
  }

  selectedMakat.value = makat
  isItemDialogOpen.value = true
}

// Check if promo department has any items (multi-item promos OR single-item promo items)
const isPromoDeptEmpty = computed(() => {
  if (selectedDepartmentId.value !== PROMO_DEPARTMENT_ID) return false
  return multiItemPromotions.value.length === 0 && singlePromoItems.value.length === 0
})

function handleSearchInput(event) {
  catalogStore.setSearchQuery(event.target.value)
}

function clearSearch() {
  catalogStore.clearSearch()
  searchOpenMobile.value = false
}

function handleSearchUpdate(query) {
  departmentSearchQuery.value = query
}

function handleRequestSubsForSearch() {
  catalogStore.loadAllSubs()
}

// ============================================
// Mobile UI Actions
// ============================================
function toggleMobileDepartments() {
  isMobileDepartmentsOpen.value = !isMobileDepartmentsOpen.value
}

// Intercept back button to close mobile departments drawer instead of navigating
useBackButtonClose(isMobileDepartmentsOpen, () => {
  isMobileDepartmentsOpen.value = false
})

function toggleDesktopSidebar() {
  desktopSidebarCollapsed.value = !desktopSidebarCollapsed.value
}

function toggleMobileSearch() {
  searchOpenMobile.value = !searchOpenMobile.value
  if (searchOpenMobile.value) {
    filtersOpenMobile.value = false
  }
}

function closeMobileSearch() {
  searchOpenMobile.value = false
}

function toggleMobileFilters() {
  filtersOpenMobile.value = !filtersOpenMobile.value
  if (filtersOpenMobile.value) {
    searchOpenMobile.value = false
  }
}

function closeMobileFilters() {
  filtersOpenMobile.value = false
}

function dismissMobileOverlays(e) {
  if (searchOpenMobile.value) {
    e.stopPropagation()
    closeMobileSearch()
  }
}

// ============================================
// Department Selection
// ============================================
function selectDepartment({ id, name }) {
  catalogStore.selectDepartment(id, name)
  if (id !== PROMO_DEPARTMENT_ID) {
    menuViewMode.value = 'subs'
    pendingAutoSelectDept.value = id
  } else {
    menuViewMode.value = 'departments'
    pendingAutoSelectDept.value = null
    if (isMobileDepartmentsOpen.value) {
      isMobileDepartmentsOpen.value = false
    }
  }
  scrollToTop()
}

// Auto-select first subcategory when department subs finish loading
watch(
  () => [visibleCurrentDeptSubs.value, subsLoading.value],
  ([subs, loading]) => {
    if (pendingAutoSelectDept.value && !loading && subs.length > 0) {
      const deptId = pendingAutoSelectDept.value
      const dept = visibleDepartments.value.find((d) => d.id === deptId)
      catalogStore.selectSubDepartment(subs[0].id, subs[0].name, deptId, dept?.name)
      pendingAutoSelectDept.value = null
    }
  }
)

function selectSubDepartment({ id, name, departmentId, departmentName }) {
  catalogStore.selectSubDepartment(id, name, departmentId, departmentName)
  if (isMobileDepartmentsOpen.value) {
    isMobileDepartmentsOpen.value = false
  }
  scrollToTop()
}

function goBackToDepartments() {
  menuViewMode.value = 'departments'
}

// ============================================
// Navigation
// ============================================
function goBackToClients() {
  router.push({ name: ROUTES.AGENTS_CLIENTS })
}

function goToClientProfile() {
  if (selectedClient.value?.id) {
    router.push({
      name: ROUTES.AGENTS_CLIENT_DETAILS,
      params: { clientId: selectedClient.value.id },
      query: { returnTo: 'catalog', clientMakat: clientMakat.value },
    })
  }
}

function dismissCartWarning() {
  showCartWarning.value = false
}

// ============================================
// Info Page Session Tracking
// ============================================
const INFO_PAGE_SHOWN_KEY = 'agent-catalog-info-shown'

function wasInfoPageShown(makat) {
  const shown = JSON.parse(sessionStorage.getItem(INFO_PAGE_SHOWN_KEY) || '[]')
  return shown.includes(makat)
}

function markInfoPageShown(makat) {
  const shown = JSON.parse(sessionStorage.getItem(INFO_PAGE_SHOWN_KEY) || '[]')
  if (!shown.includes(makat)) {
    shown.push(makat)
    sessionStorage.setItem(INFO_PAGE_SHOWN_KEY, JSON.stringify(shown))
  }
}

function handleContinueToCatalog() {
  showInfoPage.value = false
  markInfoPageShown(clientMakat.value)
}

// ============================================
// Fetch and Filter Relevant Goals (2-phase)
// Phase 1: Firestore query (can run in parallel with catalog bootstrap)
// Phase 2: Client-side filtering (needs assortmentMakats from catalog)
// ============================================
function startGoalsFetch() {
  const db = getFirebaseDb()
  const goalsRef = collection(db, 'Goals')
  const now = Timestamp.now()
  const q = query(goalsRef, where('date_end_js', '>', now), where('active', '==', true))
  return getDocs(q)
}

function filterGoalsFromSnapshot(snapshot, agentNum) {
  const assortmentSet = new Set(catalogStore.assortmentMakats)

  const filtered = []
  snapshot.forEach((doc) => {
    const goal = doc.data()

    // Agent must be assigned to this goal
    const agentInGoal = goal.agents?.some((a) => Number(a.id) === agentNum)
    if (!agentInGoal) return

    // At least one goal product must be in client's assortment
    const matchingProducts = goal.products?.filter((p) => assortmentSet.has(p.makat)) || []
    if (matchingProducts.length === 0) return

    // Sort agents by id
    if (goal.agents) {
      goal.agents.sort((a, b) => a.id - b.id)
    }

    // Attach matching products for display
    filtered.push({ ...goal, _matchingProducts: matchingProducts })
  })

  relevantGoals.value = filtered

  if (filtered.length > 0 && !wasInfoPageShown(clientMakat.value)) {
    showInfoPage.value = true
  }
}

// ============================================
// Load Client and Bootstrap Catalog
// ============================================
async function loadClientAndBootstrap() {
  if (isBootstrapRunning.value) return
  isBootstrapRunning.value = true
  isLoadingClient.value = true
  clientError.value = null
  selectedClient.value = null
  isAuthorized.value = false
  showInfoPage.value = false
  relevantGoals.value = []
  if (assortmentRequestStore.clientInfo?.makat !== clientMakat.value) {
    assortmentRequestStore.clear()
  }

  try {
    const client = await getClientByMakat(clientMakat.value)

    if (!client) {
      clientError.value = 'לקוח לא נמצא'
      return
    }

    selectedClient.value = client
    assortmentRequestStore.setClientInfo(client)

    // Admin can view any client's catalog, agents can only view their assigned clients
    if (isAdmin.value) {
      // Admin bypass - authorized for any client
      isAuthorized.value = true
    } else {
      // Agent authorization check
      const clientAgentNum = Number(client.agent)
      if (userAgentNumber.value === null) {
        clientError.value = 'לא מוגדר סוכן למשתמש'
        return
      }

      if (clientAgentNum !== userAgentNumber.value) {
        clientError.value = 'אין הרשאה לצפות באלבום של לקוח זה'
        return
      }

      isAuthorized.value = true
    }
    if (cartStore.itemCount > 0) {
      showCartWarning.value = true
    }

    // Start goals Firestore fetch in parallel with catalog bootstrap
    // The query itself doesn't need catalog data — only the filtering does
    const agentNum = userAgentNumber.value
    let goalsSnapshotPromise = null
    if (agentNum) {
      isLoadingGoals.value = true
      goalsSnapshotPromise = startGoalsFetch()
    }

    await catalogStore.bootstrap(null, { overrideClient: client })

    // Now that catalog is ready (assortmentMakats available), filter the goals
    if (goalsSnapshotPromise) {
      try {
        const snapshot = await goalsSnapshotPromise
        filterGoalsFromSnapshot(snapshot, agentNum)
      } catch (err) {
        console.error('Failed to fetch goals for info page', err)
      } finally {
        isLoadingGoals.value = false
      }
    }
  } catch (err) {
    console.error('Failed to load client for catalog', err)
    clientError.value = 'שגיאה בטעינת נתוני הלקוח'
  } finally {
    isLoadingClient.value = false
    isBootstrapRunning.value = false
  }
}

function navigateToAssortmentRequest() {
  if (selectedClient.value) {
    assortmentRequestStore.setClientInfo(selectedClient.value)
  }
  router.push({
    name: ROUTES.AGENTS_ASSORTMENT_REQUEST,
    params: { clientMakat: route.params.clientMakat },
  })
}

// Exit agent impersonation
function exitImpersonation() {
  authStore.clearTemporaryAgentOverride()
  router.push({ name: ROUTES.AGENTS_CLIENTS })
}

// ============================================
// Watchers
// ============================================
watch(
  [selectedDepartmentId, selectedSubDepartmentId],
  () => {
    // Items visibility tracking
  },
  { immediate: false }
)

watch(isSearchActive, (active) => {
  if (active) {
    scrollToTop()
  }
})

// ============================================
// Agent Impersonation Helper
// ============================================
function applyAgentImpersonation() {
  const agentOverride = route.query.agentOverride
  if (agentOverride && authStore.isAdmin) {
    const agentNum = Number(agentOverride)
    if (!isNaN(agentNum)) {
      authStore.setTemporaryAgentOverride(agentNum)
    }
  }
}

// ============================================
// Lifecycle
// ============================================
onMounted(() => {
  applyAgentImpersonation()
  lastActivatedContext.value = `${route.params.clientMakat}|${route.query.agentOverride || ''}`
  loadClientAndBootstrap()
})

// ============================================
// KeepAlive Route Guard
// Only clear state when truly leaving the agent catalog flow
// ============================================
onBeforeRouteLeave((to) => {
  const agentChildRoutes = [
    ROUTES.AGENTS_PROMOTION_DETAIL,
    ROUTES.AGENTS_QUANTITY_DISCOUNT,
    ROUTES.AGENTS_NOT_IN_ASSORTMENT,
    ROUTES.AGENTS_ASSORTMENT_REQUEST,
    ROUTES.AGENTS_CATALOG_IMMERSIVE,
  ]
  if (!agentChildRoutes.includes(to.name)) {
    // Leaving agent catalog flow — clean up
    lastActivatedContext.value = null
    authStore.clearTemporaryAgentOverride()
    catalogStore.clearAgentOverride()
  }
})

// ============================================
// KeepAlive Lifecycle
// ============================================
onActivated(() => {
  isComponentActive.value = true
  const currentContext = `${route.params.clientMakat}|${route.query.agentOverride || ''}`
  if (currentContext !== lastActivatedContext.value) {
    // Client or agent changed — clear filters from previous client and full reload
    catalogStore.resetFilters()
    menuViewMode.value = 'departments'
    pendingAutoSelectDept.value = null
    applyAgentImpersonation()
    lastActivatedContext.value = currentContext
    loadClientAndBootstrap()
  }
})

onDeactivated(() => {
  isComponentActive.value = false
})

onUnmounted(() => {
  // Safety net: fires when KeepAlive itself is destroyed (e.g., logout, non-/app route)
  authStore.clearTemporaryAgentOverride()
  catalogStore.clearAgentOverride()
})
</script>

<template>
  <div class="catalog-page">
    <!-- Agent Impersonation Banner -->
    <div v-if="authStore.isImpersonatingAgent" class="impersonation-banner">
      <div class="banner-icon">
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </div>
      <div class="banner-text">
        <strong>מצב תצוגה כסוכן</strong>
        <span
          >אתה רואה את הקטלוג כפי שסוכן מספר {{ authStore.effectiveAgentNumber }} רואה אותו</span
        >
      </div>
      <button class="banner-close" @click="exitImpersonation">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
        צא ממצב תצוגה
      </button>
    </div>

    <!-- Loading Client State -->
    <div v-if="isLoadingClient" class="loading-state">
      <div class="loading-spinner"></div>
      <p>טוען נתוני לקוח...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="clientError" class="error-state">
      <svg
        viewBox="0 0 24 24"
        width="48"
        height="48"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p class="error-text">{{ clientError }}</p>
      <button class="btn-back" @click="goBackToClients">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        חזרה לרשימת לקוחות
      </button>
    </div>

    <!-- Authorized: Show Catalog -->
    <template v-else-if="isAuthorized && selectedClient">
      <!-- Cart Warning Toast -->
      <div v-if="showCartWarning" class="cart-warning">
        <span class="warning-text">שמת לב: מעבר לקוח יכול להשפיע על מחירים/מבצעים בעגלה</span>
        <button class="btn-dismiss" @click="dismissCartWarning">הבנתי</button>
      </div>

      <!-- Goals Info Page (shown before catalog if relevant goals exist) -->
      <ClientGoalsInfoPage
        v-if="showInfoPage"
        :goals="relevantGoals"
        :client="selectedClient"
        :agent-number="userAgentNumber"
        @continue="handleContinueToCatalog"
      />

      <!-- Page Header -->
      <template v-if="!showInfoPage">
        <header class="catalog-header">
          <div class="header-content">
            <div class="header-main">
              <!-- Mobile: Switch Client before title -->
              <button class="btn-switch-client mobile-only" @click="goBackToClients">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <h1 class="page-title desktop-only">קטלוג</h1>
              <!-- Client Badge (clickable to view profile) -->
              <button class="client-badge" @click="goToClientProfile" title="צפייה בפרטי לקוח">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span class="client-name">{{ selectedClient.name }}</span>
                <span class="client-makat">({{ selectedClient.makat }})</span>
              </button>
              <!-- Selected Department/Sub Badge (Desktop) -->
              <div v-if="selectedDepartmentName" class="selected-badge desktop-only">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                </svg>
                <span>{{ selectedDepartmentName }}</span>
                <template v-if="selectedSubDepartmentName">
                  <span class="badge-separator">/</span>
                  <span>{{ selectedSubDepartmentName }}</span>
                </template>
              </div>
            </div>

            <div class="header-actions">
              <!-- Quick switch to immersive (desktop header; mobile/tablet use the cart
                   footer) — same placement + breakpoint as the client CatalogView. -->
              <div class="desktop-only">
                <ImmersiveSwitchButton variant="header" />
              </div>

              <!-- Switch Client Button (desktop only, mobile version is before page-title) -->
              <button class="btn-switch-client desktop-only" @click="goBackToClients">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                <span class="desktop-only">החלף לקוח</span>
              </button>

              <!-- Mobile: Departments Button -->
              <button class="departments-btn mobile-only" @click="toggleMobileDepartments">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                </svg>
                <span>מחלקות</span>
              </button>

              <!-- Mobile Search Button -->
              <button
                class="departments-btn mobile-only"
                @click="toggleMobileSearch"
                :class="{ active: isSearchActive }"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <span>חיפוש</span>
                <span v-if="isSearchActive" class="search-count">{{ searchResults.length }}</span>
              </button>

              <!-- Mobile Filter Button -->
              <button
                class="departments-btn mobile-only"
                @click="toggleMobileFilters"
                :class="{ active: activeFilterCount > 0 }"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                </svg>
                <span>מסננים</span>
              </button>
            </div>
          </div>

          <!-- Mobile Client Badge -->
          <div class="mobile-client-row mobile-only">
            <button class="mobile-client-badge" @click="goToClientProfile" title="צפייה בפרטי לקוח">
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span class="mobile-client-name" :style="{ fontSize: clientNameFontSize }">{{
                selectedClient.name
              }}</span>
            </button>
          </div>

          <!-- Mobile Nav Row: scrollers -->
          <div class="mobile-nav-row mobile-only">
            <MobileSubcategoryBar
              :selected-department-id="selectedDepartmentId"
              :selected-sub-department-id="selectedSubDepartmentId"
              :sub-departments="visibleCurrentDeptSubs"
              :departments="visibleDepartments"
              :promo-department-id="PROMO_DEPARTMENT_ID"
              :subs-loading="subsLoading"
              @select-subdepartment="selectSubDepartment"
              @select-department="selectDepartment"
            />
          </div>

          <!-- Mobile Search Overlay -->
          <div v-if="searchOpenMobile" class="mobile-search-overlay mobile-only">
            <div class="mobile-search-container">
              <div class="mobile-search-input-wrapper">
                <svg
                  class="search-icon"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  class="mobile-search-input"
                  placeholder="חיפוש לפי שם, מק״ט או ברקוד..."
                  :value="searchQuery"
                  @input="handleSearchInput"
                  autofocus
                />
                <button v-if="searchQuery" class="search-clear-btn" @click="clearSearch">
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p v-if="showSearchHelper" class="search-helper">הקלד לפחות 3 תווים</p>
              <p v-else-if="isSearchActive" class="search-results-count">
                נמצאו {{ searchResults.length }} פריטים
              </p>
            </div>
            <button class="mobile-search-collapse-btn" @click="closeMobileSearch">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
          </div>
        </header>

        <MobileFiltersSheet
          v-model="filtersOpenMobile"
          @navigate-not-in-assortment="navigateNotInAssortment"
        />

        <!-- Desktop Category Strip: shown only when sidebar is collapsed on PC -->
        <DesktopCategoryStrip
          v-if="desktopSidebarCollapsed"
          :selected-department-id="selectedDepartmentId"
          :selected-sub-department-id="selectedSubDepartmentId"
          :sub-departments="visibleCurrentDeptSubs"
          :departments="visibleDepartments"
          :promo-department-id="PROMO_DEPARTMENT_ID"
          :subs-loading="subsLoading"
          @select-department="selectDepartment"
          @select-subdepartment="selectSubDepartment"
        />

        <!-- Main Layout: Sidebar + Content -->
        <div class="catalog-layout">
          <!-- Desktop Sidebar with Search + Menu -->
          <aside
            class="catalog-sidebar desktop-only"
            :class="{ collapsed: desktopSidebarCollapsed }"
          >
            <!-- Collapsed State: Expand Button -->
            <div v-if="desktopSidebarCollapsed" class="sidebar-collapsed-content">
              <button class="sidebar-expand-btn" @click="toggleDesktopSidebar" title="הרחב תפריט">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <div class="collapsed-label">מחלקות</div>
            </div>

            <!-- Expanded State: Search + Menu -->
            <template v-else>
              <!-- Desktop Search Row -->
              <div class="desktop-search">
                <div class="desktop-search-row">
                  <div class="desktop-search-input-wrapper">
                    <svg
                      class="search-icon"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                      type="text"
                      class="desktop-search-input"
                      placeholder="חיפוש פריט..."
                      :value="searchQuery"
                      @input="handleSearchInput"
                    />
                    <button v-if="searchQuery" class="search-clear-btn" @click="clearSearch">
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <!-- Collapse Button -->
                  <button
                    class="sidebar-collapse-btn"
                    @click="toggleDesktopSidebar"
                    title="צמצם תפריט"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
                <p v-if="showSearchHelper" class="search-helper">הקלד לפחות 3 תווים</p>
                <p v-else-if="isSearchActive" class="search-results-count">
                  נמצאו {{ searchResults.length }} פריטים
                </p>
              </div>

              <!-- Desktop Filters -->
              <CatalogFilters @navigate-not-in-assortment="navigateNotInAssortment" />

              <!-- Departments Menu Component -->
              <DepartmentsMenu
                :departments="visibleDepartments"
                :sub-departments="visibleCurrentDeptSubs"
                :subs-by-department-id="visibleSubsByDeptIdObject"
                :catalog-items="itemsArray"
                :selected-department-id="selectedDepartmentId"
                :selected-department-name="selectedDepartmentName"
                :selected-sub-department-id="selectedSubDepartmentId"
                :selected-sub-department-name="selectedSubDepartmentName"
                :view-mode="menuViewMode"
                :is-mobile-open="isMobileDepartmentsOpen"
                :is-desktop-collapsed="desktopSidebarCollapsed"
                :is-loading="departmentsLoading"
                :subs-loading="subsLoading"
                @select-department="selectDepartment"
                @select-subdepartment="selectSubDepartment"
                @go-back="goBackToDepartments"
                @toggle-mobile="toggleMobileDepartments"
                @toggle-desktop="toggleDesktopSidebar"
                @update-search="handleSearchUpdate"
                @request-subs-for-search="handleRequestSubsForSearch"
              />
            </template>
          </aside>

          <!-- Products Container -->
          <main
            ref="productsContainerRef"
            class="products-container"
            :class="{ 'no-padding': isGridMode }"
          >
            <!-- Loading State -->
            <CatalogLoader v-if="isBootstrapping" />

            <!-- Error State -->
            <div v-else-if="bootstrapError" class="error-section">
              <div class="error-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="48"
                  height="48"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
              </div>
              <h2>שגיאה בטעינה</h2>
              <p class="text-muted">{{ bootstrapError }}</p>
              <button class="btn-retry" @click="loadClientAndBootstrap">נסה שוב</button>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="
                isPromoDeptEmpty ||
                (selectedDepartmentId !== PROMO_DEPARTMENT_ID && visibleItems.length === 0)
              "
              class="empty-section"
            >
              <div class="empty-icon">
                <svg
                  v-if="isSearchActive"
                  viewBox="0 0 24 24"
                  width="48"
                  height="48"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  width="48"
                  height="48"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </div>
              <p class="empty-text">
                {{
                  isSearchActive
                    ? 'לא נמצאו פריטים תואמים'
                    : selectedDepartmentId === PROMO_DEPARTMENT_ID
                      ? 'אין פריטים במבצעים כרגע'
                      : activeFilterCount > 0
                        ? 'אין פריטים זמינים במחלקה זו, ייתכן שחלק מהסננים הסירו את כלל הפריטים מהמחלקה.'
                        : 'אין פריטים במחלקה זו'
                }}
              </p>
            </div>

            <!-- Swipe Container (touch navigation between sub-departments) -->
            <div v-else class="swipe-container" :style="swipeStyle">
              <div
                class="items-grid"
                :class="{ 'grid-mode': isGridMode }"
                :style="gridStyle"
                @click.capture="dismissMobileOverlays"
              >
                <!-- Promotions Department: First_order promos first, then regular -->
                <template v-if="selectedDepartmentId === PROMO_DEPARTMENT_ID && !isSearchActive">
                  <!-- First_order multi-item promos -->
                  <PromotionCard
                    v-for="promo in firstOrderMultiPromos"
                    :key="'promo-' + promo.id"
                    :promotion="promo"
                    :items-per-row="validItemsPerRow"
                    :is-mobile="isMobile"
                    :is-tablet="isTablet"
                    :client-makat="clientMakat"
                    :is-grid-mode="isGridMode"
                  />
                  <!-- First_order single-item promos -->
                  <CatalogItemCard
                    v-for="item in firstOrderSinglePromoItems"
                    :key="item.makat"
                    :item="item"
                    :items-per-row="validItemsPerRow"
                    :is-mobile="isMobile"
                    :is-tablet="isTablet"
                    :is-grid-mode="isGridMode"
                    :show-prices="showPrices"
                    :goal-makats="goalMakatsSet"
                    @click="openItemDialog"
                  />
                  <!-- Regular multi-item promos -->
                  <PromotionCard
                    v-for="promo in regularMultiPromos"
                    :key="'promo-' + promo.id"
                    :promotion="promo"
                    :items-per-row="validItemsPerRow"
                    :is-mobile="isMobile"
                    :is-tablet="isTablet"
                    :client-makat="clientMakat"
                    :is-grid-mode="isGridMode"
                  />
                  <!-- Regular single-item promos -->
                  <CatalogItemCard
                    v-for="item in regularSinglePromoItems"
                    :key="item.makat"
                    :item="item"
                    :items-per-row="validItemsPerRow"
                    :is-mobile="isMobile"
                    :is-tablet="isTablet"
                    :is-grid-mode="isGridMode"
                    :show-prices="showPrices"
                    :goal-makats="goalMakatsSet"
                    @click="openItemDialog"
                  />
                </template>
                <!-- Regular departments or search results -->
                <template v-else>
                  <CatalogItemCard
                    v-for="item in visibleItems"
                    :key="item.makat"
                    :item="item"
                    :items-per-row="validItemsPerRow"
                    :is-mobile="isMobile"
                    :is-tablet="isTablet"
                    :is-grid-mode="isGridMode"
                    :show-prices="showPrices"
                    :goal-makats="goalMakatsSet"
                    @click="openItemDialog"
                  />
                </template>
              </div>
            </div>

            <!-- Swipe Tutorial (one-time hint for touch users) -->
            <SwipeTutorial v-if="!isSearchActive && !isBootstrapping" />

            <!-- Swipe transition label (absolutely positioned, visible when content is off-screen) -->
            <div v-if="transitionLabel" class="swipe-transition-label">
              <span class="swipe-transition-dept">{{ transitionLabel.deptName }}</span>
              <span class="swipe-transition-sub">{{ transitionLabel.subName }}</span>
              <svg
                class="swipe-transition-arrow"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path v-if="transitionLabel.direction === 'next'" d="M19 12H5M12 5l-7 7 7 7" />
                <path v-else d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </main>
        </div>

        <!-- Mobile Departments Drawer -->
        <Teleport to="body">
          <Transition name="drawer">
            <div
              v-if="isMobileDepartmentsOpen"
              class="mobile-drawer-overlay"
              @click="toggleMobileDepartments"
            >
              <aside class="mobile-drawer" @click.stop>
                <DepartmentsMenu
                  :departments="visibleDepartments"
                  :sub-departments="visibleCurrentDeptSubs"
                  :subs-by-department-id="visibleSubsByDeptIdObject"
                  :catalog-items="itemsArray"
                  :selected-department-id="selectedDepartmentId"
                  :selected-department-name="selectedDepartmentName"
                  :selected-sub-department-id="selectedSubDepartmentId"
                  :selected-sub-department-name="selectedSubDepartmentName"
                  :view-mode="menuViewMode"
                  :is-mobile-open="isMobileDepartmentsOpen"
                  :is-desktop-collapsed="false"
                  :is-loading="departmentsLoading"
                  :subs-loading="subsLoading"
                  @select-department="selectDepartment"
                  @select-subdepartment="selectSubDepartment"
                  @go-back="goBackToDepartments"
                  @toggle-mobile="toggleMobileDepartments"
                />
              </aside>
            </div>
          </Transition>
        </Teleport>

        <!-- Item Dialog -->
        <ItemDialog v-model:open="isItemDialogOpen" :makat="selectedMakat" />

        <!-- Assortment Request Floating CTA (desktop circle) -->
        <Transition name="fade-up">
          <button
            v-if="!niaSelectionEmpty"
            class="assortment-request-fab"
            @click="navigateToAssortmentRequest"
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            <span class="fab-count">{{ niaSelectionCount }}</span>
          </button>
        </Transition>
      </template>
    </template>
  </div>
</template>

<style scoped>
/* ============================================
   Page Layout (matching CatalogView)
   ============================================ */
.catalog-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-height));
  overflow: hidden;
}

/* ============================================
   Agent Impersonation Banner
   ============================================ */
.impersonation-banner {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-md);
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: var(--border-radius);
  margin: var(--spacing-sm) var(--spacing-md);
  box-shadow: var(--shadow-sm);
}

.banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f59e0b;
  color: white;
  border-radius: 50%;
  flex-shrink: 0;
}

.banner-icon svg {
  width: 16px;
  height: 16px;
}

.banner-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.banner-text strong {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #92400e;
}

.banner-text span {
  font-size: var(--font-size-xs);
  color: #78350f;
}

.banner-close {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px var(--spacing-sm);
  background: white;
  color: #92400e;
  border: 1px solid #f59e0b;
  border-radius: var(--border-radius);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.banner-close svg {
  width: 14px;
  height: 14px;
}

.banner-close:hover {
  background: #fef3c7;
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

@media (max-width: 767px) {
  .impersonation-banner {
    gap: var(--spacing-xs);
    padding: 6px var(--spacing-sm);
    margin: var(--spacing-xs) var(--spacing-sm);
  }

  .banner-icon {
    width: 22px;
    height: 22px;
  }

  .banner-icon svg {
    width: 12px;
    height: 12px;
  }

  .banner-text {
    gap: 4px;
    overflow: hidden;
    white-space: nowrap;
  }

  .banner-text strong {
    font-size: var(--font-size-xs);
  }

  .banner-text span {
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .banner-close {
    padding: 3px var(--spacing-xs);
    font-size: 11px;
    gap: 2px;
  }

  .banner-close svg {
    width: 12px;
    height: 12px;
  }
}

/* ============================================
   Loading State
   ============================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: var(--spacing-md);
  color: var(--text-muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   Error State
   ============================================ */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  text-align: center;
}

.error-state svg {
  color: var(--color-error);
}

.error-text {
  font-size: var(--font-size-lg);
  color: var(--text-secondary);
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-back:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* ============================================
   Cart Warning
   ============================================ */
.cart-warning {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: rgba(245, 158, 11, 0.15);
  border-bottom: 1px solid rgba(245, 158, 11, 0.3);
  flex-shrink: 0;
}

.warning-text {
  font-size: var(--font-size-sm);
  color: #92400e;
}

.btn-dismiss {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid #d97706;
  border-radius: var(--border-radius);
  background: transparent;
  color: #d97706;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  white-space: nowrap;
}

.btn-dismiss:hover {
  background: rgba(217, 119, 6, 0.1);
}

/* ============================================
   Header
   ============================================ */
.catalog-header {
  position: relative;
  flex-shrink: 0;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
}

.header-main {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.client-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid transparent;
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: #059669;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.client-badge:hover {
  background: rgba(16, 185, 129, 0.18);
  border-color: rgba(16, 185, 129, 0.3);
}

.client-badge:active {
  transform: scale(0.98);
}

.client-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-makat {
  color: var(--text-muted);
  font-size: var(--font-size-xs);
}

.selected-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
}

.badge-separator {
  color: var(--text-muted);
  margin: 0 2px;
}

/* View Controls */
/* Switch Client Button */
.btn-switch-client {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 8px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.btn-switch-client:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Mobile Nav Row: scrollers + action buttons side by side */
.mobile-nav-row {
  display: none;
  align-items: stretch;
}

/* Mobile Client Badge Row */
.mobile-client-row {
  display: none;
  padding: var(--spacing-xs) 0;
}

/* Mobile Action Controls (search + filter) */
.mobile-action-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-around;
  flex-shrink: 0;
  padding: 0 4px;
  min-width: 78px;
}

/* Mobile Client Badge */
.mobile-client-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 4px 8px;
  margin: -4px -8px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.mobile-client-badge:hover {
  background: rgba(16, 185, 129, 0.1);
}

.mobile-client-badge:active {
  background: rgba(16, 185, 129, 0.15);
}

.mobile-client-badge svg {
  flex-shrink: 0;
  color: #10b981;
}

.mobile-client-name {
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-primary);
  white-space: nowrap;
  line-height: 1.3;
}

/* Spacer to push controls to right */
.mobile-controls-spacer {
  flex: 1;
  min-width: 4px;
}

/* Mobile Departments Button */
.departments-btn {
  display: none;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 8px 14px;
  background: var(--bg-secondary);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.departments-btn:hover {
  background: var(--bg-tertiary);
}

.departments-btn:active {
  background: var(--bg-tertiary);
}

.departments-btn.active {
  background: var(--bg-tertiary);
  color: var(--color-primary);
}

.btn-badge {
  padding: 2px 8px;
  background: var(--color-primary);
  border-radius: var(--border-radius-full);
  font-size: 11px;
  color: white;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================================
   Desktop Sidebar & Search
   ============================================ */
.catalog-sidebar {
  display: flex;
  flex-direction: column;
  width: 280px;
  flex-shrink: 0;
  min-height: 0;
  background: var(--bg-primary);
  border-left: 1px solid var(--border-color);
  overflow: hidden;
  transition: width var(--transition-normal);
}

.catalog-sidebar.collapsed {
  width: 48px;
}

.sidebar-collapsed-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-sm);
  width: 100%;
}

.sidebar-expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sidebar-expand-btn:hover {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.collapsed-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin-top: var(--spacing-md);
  letter-spacing: 2px;
}

.desktop-search {
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
}

.desktop-search-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding-left: var(--spacing-xs);
}

.sidebar-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sidebar-collapse-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-color: var(--text-muted);
}

.desktop-search-input-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: 36px;
  padding: 0 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.desktop-search-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.desktop-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.desktop-search-input::placeholder {
  color: var(--text-muted);
}

.search-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.search-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.search-clear-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.search-helper {
  margin: var(--spacing-xs) 0 0;
  font-size: 12px;
  color: var(--text-muted);
  text-align: right;
}

.search-results-count {
  margin: var(--spacing-xs) 0 0;
  font-size: 12px;
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  text-align: right;
}

/* ============================================
   Mobile Search
   ============================================ */
.mobile-search-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  height: 22px;
  flex-shrink: 0;
  padding: 0 var(--spacing-sm);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.mobile-search-btn:hover {
  color: var(--color-primary);
}

.mobile-search-btn.active {
  color: var(--color-primary);
}

.search-count {
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  border-radius: var(--border-radius-full);
  padding: 0 4px;
  line-height: 1;
}

/* Mobile Filter Button */
.mobile-filter-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  height: 20px;
  flex-shrink: 0;
  padding: 0 var(--spacing-sm);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  white-space: nowrap;
}

.mobile-filter-btn:hover {
  color: var(--color-primary);
}

.mobile-filter-btn.active {
  color: var(--color-primary);
}

.filter-count {
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: var(--color-primary);
  border-radius: var(--border-radius-full);
  padding: 0 4px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.mobile-search-overlay {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-sm) var(--spacing-md);
  box-shadow: var(--shadow-md);
  z-index: 100;
  animation: slideDown 0.2s ease-out;
}

.mobile-search-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.mobile-search-collapse-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.mobile-search-collapse-btn:active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-search-container {
  flex: 1;
  min-width: 0;
}

.mobile-search-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: 42px;
  padding: 0 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.mobile-search-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.mobile-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-base);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.mobile-search-input::placeholder {
  color: var(--text-muted);
}

/* ============================================
   Main Layout
   ============================================ */
.catalog-layout {
  flex: 1;
  display: flex;
  flex-direction: row;
  min-height: 0;
  overflow: hidden;
}

/* ============================================
   Products Container
   ============================================ */
.products-container {
  position: relative;
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--spacing-lg);
  background: var(--bg-secondary);
  touch-action: pan-y;
}

.products-container.no-padding {
  padding: 0;
}

.swipe-container {
  min-height: 0;
}

.swipe-transition-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
  z-index: 0;
}

.swipe-transition-dept {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.swipe-transition-sub {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.swipe-transition-arrow {
  margin-top: 6px;
  color: var(--text-muted);
}

/* Error State */
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-2xl);
  height: 100%;
}

.error-icon {
  color: var(--color-error);
  margin-bottom: var(--spacing-md);
}

.error-section h2 {
  margin-bottom: var(--spacing-sm);
  color: var(--color-error);
}

.btn-retry {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-retry:hover {
  background: var(--color-primary-dark);
}

/* Empty State */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  height: 100%;
}

.empty-icon {
  color: var(--text-muted);
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
}

.empty-text {
  font-size: var(--font-size-base);
  color: var(--text-muted);
  margin: 0;
}

/* Items Grid */
.items-grid {
  display: grid;
  gap: var(--spacing-md);
}

.items-grid.grid-mode {
  gap: 0;
  border-radius: 0;
  overflow: hidden;
}

/* ============================================
   Mobile Drawer
   ============================================ */
.mobile-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  max-width: 85vw;
  background: var(--bg-primary);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-enter-active .mobile-drawer,
.drawer-leave-active .mobile-drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .mobile-drawer,
.drawer-leave-to .mobile-drawer {
  transform: translateX(100%);
}

/* ============================================
   Responsive
   Breakpoints: Mobile < 768px | Tablet 768-1279px | PC >= 1280px
   ============================================ */
.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

/* Tablet: 768px - 1279px */
@media (min-width: 768px) and (max-width: 1279px) {
  .catalog-header {
    padding: 4px 6px;
    border-bottom: none;
  }

  .page-title {
    font-size: var(--font-size-base);
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex;
  }

  .mobile-nav-row {
    display: flex;
    min-height: 60px;
  }

  .mobile-client-row {
    display: flex;
  }

  .products-container {
    padding: var(--spacing-md);
  }

  .products-container.no-padding {
    padding: 0;
  }

  .catalog-layout {
    flex-direction: column;
  }

  .items-grid {
    gap: var(--spacing-md);
  }
}

/* Mobile: < 768px */
@media (max-width: 767px) {
  .catalog-header {
    padding: 4px 6px;
    border-bottom: none;
  }

  .page-title {
    font-size: var(--font-size-sm);
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex;
  }

  .mobile-nav-row {
    display: flex;
  }

  .mobile-client-row {
    display: flex;
  }

  .departments-btn {
    display: flex;
  }

  .products-container {
    padding: var(--spacing-sm);
  }

  .products-container.no-padding {
    padding: 0;
  }

  .catalog-layout {
    flex-direction: column;
  }

  .items-grid {
    gap: var(--spacing-sm);
  }

  /* Hide desktop client badge on mobile - shown in mobile-client-row instead */
  .client-badge {
    display: none;
  }

  .btn-switch-client {
    padding: 6px 10px;
  }

  .cart-warning {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-md);
  }
}

/* ============================================
   Assortment Request Floating CTA
   ============================================ */
.assortment-request-fab {
  position: fixed;
  bottom: 24px;
  left: 90px;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: var(--color-success);
  color: white;
  border: none;
  border-radius: var(--border-radius-full);
  cursor: pointer;
  box-shadow:
    0 4px 12px rgba(16, 185, 129, 0.35),
    0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all var(--transition-fast);
}

.assortment-request-fab:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow:
    0 6px 20px rgba(16, 185, 129, 0.45),
    0 4px 8px rgba(0, 0, 0, 0.15);
}

.assortment-request-fab:active {
  transform: translateY(0);
}

.fab-count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: var(--color-success);
  border: 2px solid var(--color-success);
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

/* Transition */
.fade-up-enter-active {
  transition: all 0.3s ease-out;
}
.fade-up-leave-active {
  transition: all 0.2s ease-in;
}
.fade-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (max-width: 1023px) {
  .catalog-page {
    height: calc(100vh - var(--header-height) - var(--mobile-footer-height));
  }

  .assortment-request-fab {
    display: none;
  }

  .products-container {
    padding-bottom: var(--spacing-md);
  }
}
</style>
